/**
 * Cookie Storage Utilities
 */

import { CartState } from '../types/cart.types';
import { CART_CONSTANTS } from '../config/constants';
import { ProductContext } from '../product/product.context';
import { CartKeyService } from '../services/CartKeyService';

export class StorageUtils {
  /**
   * Check if we're in a lower environment (dev/staging/localhost)
   * @returns true if in lower environment, false otherwise
   */
  private static isLowerEnvironment(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const hostname = window.location.hostname.toLowerCase();
    
    // Check for localhost
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return true;
    }
    
    // Check for dev or stg prefixes
    const hostPrefix = hostname.split('.')[0].split('-')[0];
    if (hostPrefix === 'dev' || hostPrefix === 'stg') {
      return true;
    }
    
    // Check for staging domains
    if (hostname.includes('stg-') || hostname.includes('staging')) {
      return true;
    }
    
    return false;
  }

  /**
   * Get the cookie domain based on the current page domain URL
   * @returns Domain string or empty string for current domain
   */
  private static getCookieDomain(): string {
    if (typeof window === 'undefined') {
      return '';
    }
    
    const hostname = window.location.hostname.toLowerCase();
    const isLowerEnv = this.isLowerEnvironment();
    
    // For localhost, don't set domain (cookies work fine without it)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return '';
    }
    
    // Extract root domain from current page URL
    // This allows cookies to be shared across subdomains
    const parts = hostname.split('.');
    
    // For IP addresses, don't set domain
    if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      return '';
    }
    
    // For domains with at least 2 parts (e.g., 'example.com', 'www.example.com')
    if (parts.length >= 2) {
      // Extract root domain (last 2 parts)
      // Examples:
      // 'www.example.com' -> '.example.com'
      // 'shop.nvidia.com' -> '.nvidia.com'
      // 'dev.example.co.uk' -> '.co.uk' (but we want '.example.co.uk')
      // For most cases, last 2 parts work, but for TLDs like .co.uk, we might need 3
      
      // Check for common multi-part TLDs
      const multiPartTlds = ['co.uk', 'com.au', 'co.nz', 'com.br', 'co.jp'];
      const lastTwoParts = parts.slice(-2).join('.');
      
      if (multiPartTlds.includes(lastTwoParts) && parts.length >= 3) {
        // For multi-part TLDs, use last 3 parts
        return '.' + parts.slice(-3).join('.');
      }
      
      // Standard case: use last 2 parts (e.g., 'example.com')
      return '.' + parts.slice(-2).join('.');
    }
    
    // For single-part domains or if extraction fails, don't set domain
    return '';
  }

  /**
   * Set a cookie
   * @param name Cookie name
   * @param value Cookie value
   * @param days Days until expiration (default: 1 - 24 hours)
   * @param path Cookie path (default: '/')
   * @param domain Cookie domain (optional, auto-detected if not provided)
   */
  static setCookie(name: string, value: string, days: number = 1, path: string = '/', domain?: string): void {
    try {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      const expires = `expires=${date.toUTCString()}`;
      
      const isLowerEnv = this.isLowerEnvironment();
      
      // ✅ Compress cartKey cookie ONLY using CartKeyService
      // Note: cartKey is a variable that can be 'nvHeadless' or other values depending on config
      let cookieValue: string;
      if (name === 'nvHeadless' || name === 'cartKey') {
        try {
          const parsedValue = JSON.parse(value);
          const compressedValue = CartKeyService.compress(parsedValue, days);
          cookieValue = encodeURIComponent(compressedValue);
        } catch (error) {
          cookieValue = encodeURIComponent(value);
        }
      } else {
        // For other cookies, just encode normally
        cookieValue = encodeURIComponent(value);
      }
      
      // Build cookie string with SameSite attribute for better compatibility
      // Use Lax for first-party cookies (cookies set on same domain)
      // SameSite=None requires HTTPS and is only needed for cross-site cookies
      const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
      const isHttps = protocol === 'https:';
      
      // For production HTTPS sites, use Lax for better cookie retention
      // For HTTP or lower environments, use Lax without Secure flag
      const sameSite = isHttps && !isLowerEnv ? 'SameSite=Lax;Secure' : 'SameSite=Lax';
      
      // Get domain (use provided domain or auto-detect)
      const cookieDomain = domain !== undefined ? domain : this.getCookieDomain();
      const domainAttr = cookieDomain ? `domain=${cookieDomain};` : '';
      
      document.cookie = `${name}=${cookieValue};${expires};path=${path};${domainAttr}${sameSite}`;
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Get a cookie value
   * @param name Cookie name
   * @returns Parsed cookie value or null
   */
  static getCookie(name: string): any {
    try {
      const nameEQ = name + '=';
      const ca = document.cookie.split(';');
      const isLowerEnv = this.isLowerEnvironment();
      
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
          c = c.substring(1, c.length);
        }
        if (c.indexOf(nameEQ) === 0) {
          let value = c.substring(nameEQ.length, c.length);
          const rawStoredValue = value; // Store raw value as stored in browser
          
          // Always decode cookie value
          const decodedValue = decodeURIComponent(value);
          
          // ✅ Decompress cart cookie ONLY using CartKeyService
          // Note: cartKey is a variable that can be 'nvHeadless' or other values depending on config
          if (name === 'nvHeadless' || name === 'cartKey') {
            try {
              const decompressedValue = CartKeyService.decompress(decodedValue);
              return decompressedValue;
            } catch (error) {
              // Fallback: try to parse as regular JSON
              try {
                return JSON.parse(decodedValue);
            } catch {
                return decodedValue;
              }
            }
          }
          
          try {
            return JSON.parse(decodedValue);
          } catch {
            return decodedValue;
          }
        }
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get a plain cookie value (without JSON parsing)
   * @param name Cookie name
   * @returns Raw cookie value or null
   */
  static getPlainCookie(name: string): string | null {
    try {
      const nameEQ = name + '=';
      const ca = document.cookie.split(';');
      
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
          c = c.substring(1, c.length);
        }
        if (c.indexOf(nameEQ) === 0) {
          return decodeURIComponent(c.substring(nameEQ.length, c.length));
        }
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Delete a cookie
   * @param name Cookie name
   * @param path Cookie path (default: '/')
   * @param domain Cookie domain (optional, auto-detected if not provided)
   */
  static deleteCookie(name: string, path: string = '/', domain?: string): void {
    try {
      const cookieDomain = domain !== undefined ? domain : this.getCookieDomain();
      const domainAttr = cookieDomain ? `domain=${cookieDomain};` : '';
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=${path};${domainAttr}`;
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Save cart state to cookies
   */
  static saveCartState(state: CartState): void {
    try {
      const serialized = JSON.stringify(state);
      this.setCookie(CART_CONSTANTS.STORAGE_KEYS.CART_STATE, serialized);
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Load cart state from cookies
   */
  static loadCartState(): CartState | null {
    try {
      const state = this.getCookie(CART_CONSTANTS.STORAGE_KEYS.CART_STATE);
      return state as CartState | null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Clear cart state from cookies
   */
  static clearCartState(): void {
    try {
      this.deleteCookie(CART_CONSTANTS.STORAGE_KEYS.CART_STATE);
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Generic cookie getter with JSON parsing
   */
  static getItem<T>(key: string): T | null {
    return this.getCookie(key) as T | null;
  }

  /**
   * Generic cookie setter
   */
  static setItem<T>(key: string, value: T, days: number = 365): void {
    const serialized = JSON.stringify(value);
    this.setCookie(key, serialized, days);
  }

  /**
   * Generic cookie remover
   */
  static removeItem(key: string): void {
    this.deleteCookie(key);
  }

  /**
   * Set cart data (used by old library pattern)
   * @param key Cookie key name
   * @param value Cookie value
   * @param domain Optional domain (defaults to auto-detected domain from page URL)
   * @param days Days until expiration (default: 1 - 24 hours)
   */
  static setCartData(key: string, value: string, domain?: string, days: number = 1): void {
    // If domain is not provided, use auto-detected domain from current page URL
    const cookieDomain = domain !== undefined ? domain : this.getCookieDomain();
    this.setCookie(key, value, days, '/', cookieDomain);
  }

  /**
   * Get cart data (used by old library pattern)
   */
  static getCartData(key: string): any {
    return this.getCookie(key);
  }

  /**
   * Check if a cookie exists
   */
  static hasCookie(name: string): boolean {
    return this.getCookie(name) !== null;
  }

  /**
   * Get all cookies as an object
   */
  static getAllCookies(): Record<string, string> {
    const cookies: Record<string, string> = {};
    const ca = document.cookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      const c = ca[i].trim();
      const [name, ...valueParts] = c.split('=');
      if (name && valueParts.length > 0) {
        cookies[name] = decodeURIComponent(valueParts.join('='));
      }
    }
    
    return cookies;
  }

  /**
   * Clear all cookies
   */
  static clearAllCookies(): void {
    const cookies = this.getAllCookies();
    Object.keys(cookies).forEach(name => {
      this.deleteCookie(name);
    });
  }

  // ==================== SessionStorage Methods ====================

  /**
   * Set an item in sessionStorage
   * @param key Storage key
   * @param value Value to store (will be JSON stringified)
   */
  static setSessionItem<T>(key: string, value: T): void {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Get an item from sessionStorage
   * @param key Storage key
   * @returns Parsed value or null
   */
  static getSessionItem<T>(key: string): T | null {
    try {
      const item = sessionStorage.getItem(key);
      if (item === null) {
        return null;
      }
      try {
        return JSON.parse(item) as T;
      } catch {
        return item as T;
      }
    } catch (error) {
      return null;
    }
  }

  /**
   * Get a plain sessionStorage value (without JSON parsing)
   * @param key Storage key
   * @returns Raw value or null
   */
  static getPlainSessionItem(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  /**
   * Remove an item from sessionStorage
   * @param key Storage key
   */
  static removeSessionItem(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Check if a sessionStorage item exists
   * @param key Storage key
   */
  static hasSessionItem(key: string): boolean {
    try {
      return sessionStorage.getItem(key) !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Clear all items from sessionStorage
   */
  static clearSessionStorage(): void {
    try {
      sessionStorage.clear();
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Get all sessionStorage items as an object
   */
  static getAllSessionItems(): Record<string, string> {
    const items: Record<string, string> = {};
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key) {
          const value = sessionStorage.getItem(key);
          if (value !== null) {
            items[key] = value;
          }
        }
      }
    } catch (error) {
      // Silently fail
    }
    return items;
  }

  /**
   * Save cart state to sessionStorage
   */
  static saveCartStateToSession(state: CartState): void {
    try {
      const serialized = JSON.stringify(state);
      sessionStorage.setItem(CART_CONSTANTS.STORAGE_KEYS.CART_STATE, serialized);
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Load cart state from sessionStorage
   */
  static loadCartStateFromSession(): CartState | null {
    try {
      const serialized = sessionStorage.getItem(CART_CONSTANTS.STORAGE_KEYS.CART_STATE);
      if (serialized === null) {
        return null;
      }
      return JSON.parse(serialized) as CartState;
    } catch (error) {
      return null;
    }
  }

  /**
   * Clear cart state from sessionStorage
   */
  static clearCartStateFromSession(): void {
    try {
      sessionStorage.removeItem(CART_CONSTANTS.STORAGE_KEYS.CART_STATE);
    } catch (error) {
      // Silently fail
    }
  }

  // ==================== Hybrid Storage Methods ====================

  /**
   * Save to both cookie and sessionStorage
   */
  static saveToBoth<T>(key: string, value: T, cookieDays: number = 365): void {
    this.setItem(key, value, cookieDays);
    this.setSessionItem(key, value);
  }

  /**
   * Get from cookie, fallback to sessionStorage
   */
  static getFromEither<T>(key: string): T | null {
    const cookieValue = this.getCookie(key);
    if (cookieValue !== null) {
      return cookieValue as T;
    }
    return this.getSessionItem<T>(key);
  }

  /**
   * Remove from both cookie and sessionStorage
   */
  static removeFromBoth(key: string): void {
    this.deleteCookie(key);
    this.removeSessionItem(key);
  }

  /**
   * Clear all storage (cookies and sessionStorage)
   */
  static clearAllStorage(): void {
    this.clearAllCookies();
    this.clearSessionStorage();
  }
}

